/**
 * navigation.ts — Portal navigation destinations + helpers.
 *
 * Two output shapes (selectable per consumer):
 *   - `getNavigation(context)` → flat array (legacy, used by old AppHeader)
 *   - `getNavigationGrouped(context)` → array of sections, each with items
 *     (used by new <Sidebar> / <SidebarDrawer> from N-19 sidebar migration)
 *
 * Public contexts keep a flat shape (single Beranda section) since the
 * public pages don't render a sidebar — only the top header nav.
 */

export type PortalContext = 'public' | 'student' | 'tutor' | 'admin' | 'parent';

export interface ShellUser {
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'tutor' | 'player' | 'parent';
  kind: 'admin' | 'tutor' | 'student' | 'player' | 'parent' | 'visitor';
  isApprovedTutor: boolean;
}

export interface NavigationDestination {
  label: string;
  href: string | null;
  icon:
    | 'home'
    | 'learn'
    | 'class'
    | 'progress'
    | 'students'
    | 'tutor'
    | 'student'
    | 'sync'
    | 'activity'
    | 'worksheet'
    | 'parent'
    | 'game'
    | 'report'
    | 'lesson'
    | 'help';
  exact?: boolean;
  matchPrefixes?: string[];
  disabledReason?: string;
  badge?: string | number;
}

/**
 * A grouped view used by the sidebar. Each section renders its items
 * underneath a small uppercase label. Items inside a section are flat
 * (no nested sub-menus in MVP). An optional `section` on
 * NavigationDestination marks the section name; absent items fall into a
 * default "Umum" section at the top.
 */
export interface NavigationSection {
  /** Section display label (uppercase via CSS). */
  label: string;
  items: NavigationDestination[];
}

const navigation: Record<PortalContext, NavigationDestination[]> = {
  public: [
    { label: 'Beranda', href: '/', icon: 'home', exact: true },
    { label: 'Game', href: '/games', icon: 'learn', matchPrefixes: ['/games'] },
    { label: 'Worksheet', href: '/worksheets', icon: 'worksheet', matchPrefixes: ['/worksheets'] },
  ],
  student: [
    { label: 'Beranda', href: '/me', icon: 'home', exact: true },
    { label: 'Belajar', href: '/games', icon: 'learn', matchPrefixes: ['/games'] },
    { label: 'Worksheet', href: '/worksheets', icon: 'worksheet', matchPrefixes: ['/worksheets'] },
    { label: 'Kelas', href: '/join', icon: 'class', matchPrefixes: ['/join'] },
    { label: 'Progres', href: '/me/progress', icon: 'progress', matchPrefixes: ['/me/progress'] },
  ],
  tutor: [
    { label: 'Beranda', href: '/dashboard', icon: 'home', exact: true },
    {
      label: 'Kelas',
      href: '/dashboard/classes',
      icon: 'class',
      matchPrefixes: ['/dashboard/classes'],
    },
    { label: 'Game', href: '/games', icon: 'learn', matchPrefixes: ['/games'] },
    { label: 'Worksheet', href: '/worksheets', icon: 'worksheet', matchPrefixes: ['/worksheets'] },
    {
      label: 'Lesson plan',
      href: '/lesson-plans',
      icon: 'lesson',
      matchPrefixes: ['/lesson-plans'],
    },
    {
      label: 'Siswa',
      href: '/dashboard/students',
      icon: 'students',
      matchPrefixes: ['/dashboard/students'],
    },
    {
      label: 'Panduan leveling',
      href: '/dashboard/leveling-guide',
      icon: 'help',
      matchPrefixes: ['/dashboard/leveling-guide'],
    },
    {
      label: 'Panduan sistem badge',
      href: '/dashboard/panduan-badges',
      icon: 'help',
      matchPrefixes: ['/dashboard/panduan-badges'],
    },
  ],
  admin: [
    { label: 'Beranda', href: '/admin', icon: 'home', exact: true },
    { label: 'Sinkronisasi', href: '/admin/sync', icon: 'sync', matchPrefixes: ['/admin/sync'] },
    { label: 'Aktivitas', href: '/admin/audit', icon: 'activity', matchPrefixes: ['/admin/audit'] },
    { label: 'Tutor', href: '/admin/tutors', icon: 'tutor', matchPrefixes: ['/admin/tutors'] },
    {
      label: 'Siswa',
      href: '/admin/students',
      icon: 'student',
      matchPrefixes: ['/admin/students'],
    },
    {
      label: 'Orang tua',
      href: '/admin/parents',
      icon: 'parent',
      matchPrefixes: ['/admin/parents'],
    },
    // N-10 + N-11 destinations live here so when those routes ship, the
    // sidebar already references them. They render but the link href is
    // null until the routes are implemented (see N-10 / N-11 plan).
    {
      label: 'Game',
      href: '/admin/games',
      icon: 'game',
      matchPrefixes: ['/admin/games'],
    },
    {
          label: 'Worksheet',
          href: '/admin/worksheets',
          icon: 'worksheet',
          matchPrefixes: ['/admin/worksheets'],
        },
      ],
      parent: [
        // N-17: parent dashboard ships the children list at /parent. The
        // per-child detail view (/parent/children/$studentId) is reached via
        // drill-down from there, not as a sidebar item.
        { label: 'Beranda', href: '/parent', icon: 'home', exact: true },
      ],
    };

/**
 * Section grouping config per context. Items that don't appear in any section
 * are dropped from the sidebar (they still render in the legacy top nav if
 * `getNavigation` is consumed there). Order here determines visual order.
 */
const sectionsByContext: Record<PortalContext, NavigationSection[]> = {
  public: [
    {
      label: 'Jelajahi',
      items: navigation.public,
    },
  ],
  student: [
    {
      label: 'Belajar',
      items: navigation.student.filter((d) =>
        ['Beranda', 'Belajar', 'Worksheet', 'Kelas', 'Progres'].includes(d.label),
      ),
    },
  ],
  tutor: [
    {
      label: 'Utama',
      items: navigation.tutor.filter((d) => d.label === 'Beranda'),
    },
    {
      label: 'Kelas & siswa',
      items: navigation.tutor.filter((d) => ['Kelas', 'Siswa'].includes(d.label)),
    },
    {
      label: 'Konten',
      items: navigation.tutor.filter((d) => ['Game', 'Worksheet', 'Lesson plan'].includes(d.label)),
    },
    {
      label: 'Panduan',
      items: navigation.tutor.filter((d) =>
        ['Panduan leveling', 'Panduan sistem badge'].includes(d.label),
      ),
    },
  ],
  admin: [
      {
        label: 'Operasional',
        items: navigation.admin.filter((d) =>
          ['Beranda', 'Sinkronisasi', 'Aktivitas'].includes(d.label),
        ),
      },
      {
        label: 'Manajemen',
        items: navigation.admin.filter((d) =>
          ['Tutor', 'Siswa', 'Orang tua', 'Game', 'Worksheet'].includes(d.label),
        ),
      },
    ],
    parent: [
      {
        label: 'Orang tua',
        items: navigation.parent,
      },
    ],
  };

export function getPortalContext(user: ShellUser | null): PortalContext {
  if (!user) return 'public';
  if (user.role === 'admin') return 'admin';
  if (user.role === 'tutor' && user.isApprovedTutor) return 'tutor';
  if (user.role === 'parent') return 'parent';
  if (user.kind === 'student') return 'student';
  return 'public';
}

export function getNavigation(context: PortalContext): NavigationDestination[] {
  return navigation[context];
}

/**
 * Get the navigation as grouped sections for the sidebar. Sections with
 * zero items are dropped automatically so empty groups don't render.
 */
export function getNavigationGrouped(context: PortalContext): NavigationSection[] {
  return sectionsByContext[context]
    .map((section) => ({ ...section, items: [...section.items] }))
    .filter((section) => section.items.length > 0);
}

export function getHomeHref(context: PortalContext): string {
  return {
    public: '/',
    student: '/me',
    tutor: '/dashboard',
    admin: '/admin',
    parent: '/parent',
  }[context];
}

export function isDestinationActive(item: NavigationDestination, pathname: string): boolean {
  if (!item.href) return false;
  const hrefPath = item.href.split('#')[0];
  if (item.exact) return pathname === hrefPath;
  return item.matchPrefixes?.some((prefix) => pathname.startsWith(prefix)) ?? false;
}
